import { djs } from "@/lib/djs";

type Maybe = string | null | undefined;
type Moment = Date | string | null | undefined;

const OPERATION_ZONE = "America/Sao_Paulo";

const OBJECT_ID = /^[0-9a-f]{24}$/iu;

function instant(value: Date | string) {
	if (typeof value !== "string") {
		return djs(value).tz(OPERATION_ZONE);
	}

	if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/u.test(value)) {
		return djs.utc(`${value.replace(" ", "T")}Z`).tz(OPERATION_ZONE);
	}

	return djs(value).tz(OPERATION_ZONE);
}

function normalizeReais(clean: string) {
	if (clean.includes(",")) {
		return clean.replaceAll(".", "").replace(",", ".");
	}

	if (/^-?\d+\.\d{1,2}$/u.test(clean)) {
		return clean;
	}

	return clean.replaceAll(".", "");
}

export const format = {
	today() {
		return djs().tz(OPERATION_ZONE).format("YYYY-MM-DD");
	},

	date(value: Moment) {
		if (!value) {
			return null;
		}

		if (typeof value === "string") {
			return djs(value.slice(0, 10)).format("DD/MM/YYYY");
		}

		return instant(value).format("DD/MM/YYYY");
	},

	localDate(value: string) {
		return djs(value.slice(0, 10)).format("DD/MM/YYYY");
	},

	dateTime(value: Moment) {
		if (!value) {
			return null;
		}

		return instant(value).format("DD/MM/YYYY HH:mm");
	},

	recordingMoment(value: Moment) {
		if (!value) {
			return null;
		}

		const at = instant(value).locale("pt-br");
		const sameYear = at.year() === djs().tz(OPERATION_ZONE).year();

		return at.format(sameYear ? "D [de] MMMM, HH:mm" : "D [de] MMMM [de] YYYY, HH:mm");
	},

	monthYear(value: Moment) {
		if (!value) {
			return null;
		}

		return instant(value).locale("pt-br").format("MMMM [de] YYYY");
	},

	time(value: Moment) {
		if (!value) {
			return null;
		}

		return instant(value).format("HH:mm");
	},

	phone(norm: Maybe, raw?: Maybe) {
		if (!norm) {
			if (!raw) {
				return null;
			}

			return raw;
		}

		if (norm.length === 11) {
			return `(${norm.slice(0, 2)}) ${norm.slice(2, 7)}-${norm.slice(7)}`;
		}

		if (norm.length === 10) {
			return `(${norm.slice(0, 2)}) ${norm.slice(2, 6)}-${norm.slice(6)}`;
		}

		return `+${norm}`;
	},

	cpf(cpf: Maybe) {
		if (!cpf) {
			return null;
		}

		if (cpf.length !== 11) {
			return cpf;
		}

		return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
	},

	cep(value: Maybe) {
		if (!value) {
			return null;
		}

		if (value.length === 8) {
			return `${value.slice(0, 5)}-${value.slice(5)}`;
		}

		return value;
	},

	conflictValue(field: string, value: Maybe) {
		if (!value) {
			return null;
		}

		if (field === "phone") {
			return format.phone(value);
		}

		if (field === "cpf") {
			return format.cpf(value);
		}

		if (field === "birth_date") {
			return value.slice(0, 10);
		}

		if (field === "address" || field === "cep") {
			return /^\d{8}$/u.test(value) ? format.cep(value) : value;
		}

		return value;
	},

	orDash(value: Maybe) {
		if (!value) {
			return "-";
		}

		return value;
	},

	readableName(name: Maybe) {
		const trimmed = name?.trim();

		if (!trimmed || OBJECT_ID.test(trimmed) || !/\p{L}/u.test(trimmed)) {
			return null;
		}

		return trimmed;
	},

	centsFromReais(text: string): number | null {
		const clean = text
			.trim()
			.replace(/^R\$\s*/u, "")
			.replaceAll(/\s/gu, "");

		if (clean.length === 0) {
			return null;
		}

		const normalized = normalizeReais(clean);

		if (!/^-?\d+(\.\d{1,2})?$/u.test(normalized)) {
			return null;
		}

		return Math.round(Number(normalized) * 100);
	},
};
